/**
 * Editor header submit control — readiness AND lifecycle.
 *
 * The original was lifecycle-blind, and that was the bug: on recipe 1, whose eight processes
 * were all signed off, it cheerfully offered "Submit for approval". You cannot submit what is
 * already approved, and you cannot re-submit what is already sitting in a reviewer's queue.
 * The server now says so in one field, and this control obeys it:
 *
 *   lifecycle.submit_blocked_by === 'lifecycle' →  a state PILL, not a button.
 *                                            `submit_unavailable_reason` is the tooltip, in the
 *                                            server's own words.
 *   blockers, lifecycle allows submission →  "Submit — N blockers", click lists them in the rail
 *   clean, draft                          →  "Submit for approval"
 *   clean, returned                       →  "Re-submit for review"  (the loop's closing move)
 *
 * Two different submissions, deliberately kept apart:
 *
 *   `/bp/mbmr/resubmit/`            puts the record (back) under review. This is what a draft
 *                                   or a returned record needs, and it is new — see below.
 *   `send_recipe_approval_status`   is the MOVE-TO-PRODUCTION gate that ViewRecipe's button
 *                                   also calls. It writes a `Recipe_Approval_status` row and
 *                                   refuses outright unless the recipe already has a trial
 *                                   batch that has finished; it never touches
 *                                   `ProcessApprovalStatus`. Pointing "submit for review" at
 *                                   it — which is what this button used to do — is why a
 *                                   returned record stayed returned however many times it was
 *                                   pressed. It is still offered, as its own action, once the
 *                                   record is actually approved.
 */
import React, { useState } from 'react';
import { Modal, Tooltip } from 'antd';
import { toast } from 'react-toastify';
import { AlertTriangle, Send, Undo2 } from 'lucide-react';
import { useTheme } from '@/App';
import { defaultColors } from '@/themes/antd-theme';
import useModuleAccess from '@/hooks/useModuleAccess';
import { resubmitForReview } from '@/utils/mbmr_readiness_api';
import { totalBlocking } from './readinessModel';
import { useReadiness } from './ReadinessContext';
import { ApprovalStatePill } from './ApprovalStateIcon';

export default function ReadinessSubmitButton({ recipeId }) {
  const { isDark } = useTheme();
  const { model, loading, openBlockers, source, lifecycle, applyLifecycle, reload } = useReadiness() || {};
  const access = useModuleAccess('Master Records (MBR / MPR)');
  const [submitting, setSubmitting] = useState(false);

  if (!recipeId) return null;

  const summary = model?.summary;
  // Operations AND the record. Reading `blocking_count` alone offered a green "Submit for
  // approval" on a record the server refuses — the record's own findings (its order unstated,
  // a bill-of-materials line nothing charges) block submission and count for nothing here.
  const blocking = totalBlocking(summary);
  const recordBlocking = summary?.record_blocking_count ?? 0;
  const clean = Boolean(summary) && blocking === 0;
  const canSubmit = access?.create || access?.update || access?.isAdmin;

  if (loading || !summary) {
    return (
      <span style={{ fontSize: 12.5, color: isDark ? defaultColors.palette.gray[400] : '#6b7280' }}>
        Checking readiness…
      </span>
    );
  }

  // ── the record's STATE forbids submission ────────────────────────────────
  // A pill, not a disabled button: a disabled button still says "this is the action here,
  // you just can't have it". An approved MBMR has no submit action at all, and the honest
  // rendering is a status read-out with the reason attached.
  //
  // Only a LIFECYCLE refusal renders this way. A readiness refusal — "1 operation still has
  // a blocking gap" on a draft — is a to-do list, and the control has to stay the button
  // that opens it, or the author is told no with nowhere to go.
  if (lifecycle?.known && lifecycle.submit_blocked_by === 'lifecycle') {
    return (
      <Tooltip title={lifecycle.submit_unavailable_reason || lifecycle.editable_reason}>
        <span>
          <ApprovalStatePill state={lifecycle.state}>
            {lifecycle.state === 'approved'
              ? blocking > 0
                ? `Approved · ${blocking} finding${blocking === 1 ? '' : 's'}`
                : 'Approved'
              : lifecycle.label}
          </ApprovalStatePill>
        </span>
      </Tooltip>
    );
  }

  const returned = lifecycle?.known && lifecycle.state === 'returned';
  const bg = clean
    ? isDark
      ? defaultColors.successLight
      : defaultColors.success
    : isDark
    ? defaultColors.errorLight
    : defaultColors.error;

  // When every operation is clean and only the record is not, say so: "Submit — 1 blocker"
  // would send the author hunting through operations that are all green.
  const onlyRecord = blocking > 0 && (summary?.blocking_count ?? 0) === 0;
  const label = clean
    ? returned
      ? 'Re-submit for review'
      : 'Submit for approval'
    : onlyRecord
    ? `Submit — ${recordBlocking} record finding${recordBlocking === 1 ? '' : 's'}`
    : `Submit — ${blocking} blocker${blocking === 1 ? '' : 's'}`;

  const doSubmit = async () => {
    setSubmitting(true);
    try {
      const body = await resubmitForReview({ recipe_id: recipeId });
      applyLifecycle?.(body?.data?.lifecycle);
      reload?.();
      toast.success(body?.message || 'Sent for review');
    } catch (e) {
      // The server's refusal is a rule (a blocker count, an already-open review); show it.
      toast.warning(e?.message || 'The MBMR could not be sent for review');
    } finally {
      setSubmitting(false);
    }
  };

  const onClick = () => {
    if (!clean) {
      // Never fail silently: show exactly which operations are in the way.
      openBlockers?.();
      return;
    }
    Modal.confirm({
      title: returned ? 'Re-submit this MBMR for review?' : 'Submit this MBMR for approval?',
      okText: returned ? 'Re-submit' : 'Submit for approval',
      cancelText: 'Cancel',
      content: (
        <div style={{ fontSize: 13, lineHeight: 1.5 }}>
          <p style={{ marginBottom: 8 }}>
            All {summary.operations_total} operations pass their readiness checks. This puts every
            process back to <em>Under Review</em> and notifies the reviewers configured on it.
          </p>
          <p style={{ marginBottom: 0, opacity: 0.75 }}>
            Moving the recipe into production is a separate step, taken once the review is complete.
          </p>
        </div>
      ),
      onOk: doSubmit,
    });
  };

  const disabled = submitting || (clean && !canSubmit);

  return (
    <Tooltip
      title={
        clean && !canSubmit
          ? 'You do not have permission to submit this MBMR for approval.'
          : clean
          ? returned
            ? 'This MBMR was returned for correction. Every operation now passes its readiness checks — send it back for review.'
            : 'Every operation passes its readiness checks'
          : `${blocking} operation${blocking === 1 ? '' : 's'} still block submission — click to list them${
              source === 'mock' ? ' (readiness preview: the endpoint is not live yet)' : ''
            }`
      }
    >
      <button
        onClick={onClick}
        disabled={disabled}
        className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-semibold rounded-md text-white shadow-sm transform-gpu transition-transform duration-200 hover:scale-105 disabled:opacity-60 disabled:hover:scale-100"
        style={{ backgroundColor: bg, whiteSpace: 'nowrap' }}
      >
        {!clean ? (
          <AlertTriangle className="w-3.5 h-3.5" />
        ) : returned ? (
          <Undo2 className="w-3.5 h-3.5" />
        ) : (
          <Send className="w-3.5 h-3.5" />
        )}
        {label}
      </button>
    </Tooltip>
  );
}
